import { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Dialog } from '@/components/common/Dialog';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';
import type { CanonicalDriver } from './DriversTab';

interface RaceDriver {
  id: string;
  firstName: string;
  lastName: string;
  nationality: string;
}

interface SuggestedRaceDriver extends RaceDriver {
  similarity: number;
}

interface SuggestedSessionDriver {
  id: string;
  displayName: string;
  similarity: number;
}

interface Props {
  open: boolean;
  editing: CanonicalDriver | null;
  saving: boolean;
  onSave: (values: { canonicalDriverId: string; aliasDriverIds: string[]; sessionDriverIds: string[] }) => void;
  onClose: () => void;
}

const PAGE_SIZE = 10;

/** Threshold above which a driver is pre-selected as a likely alias. */
const SIMILARITY_THRESHOLD = 0.6;

export function DriverFormDialog({ open, editing, saving, onSave, onClose }: Props) {
  const { t } = useI18n();

  // Wizard step: 0 = select canonical, 1 = select aliases
  const [step, setStep] = useState(0);

  const [canonicalDriverId, setCanonicalDriverId] = useState('');
  const [aliasDriverIds, setAliasDriverIds] = useState<string[]>([]);
  const [sessionDriverIds, setSessionDriverIds] = useState<string[]>([]);

  const [allRaceDrivers, setAllRaceDrivers] = useState<RaceDriver[]>([]);
  const [suggestedRaceDrivers, setSuggestedRaceDrivers] = useState<SuggestedRaceDriver[]>([]);
  const [suggestedSessionDrivers, setSuggestedSessionDrivers] = useState<SuggestedSessionDriver[]>([]);

  // Step 1 state
  const [canonicalSearch, setCanonicalSearch] = useState('');
  const [canonicalPage, setCanonicalPage] = useState(0);

  // Step 2 state
  const [raceSearch, setRaceSearch] = useState('');
  const [sessionSearch, setSessionSearch] = useState('');

  // Track whether we've auto-preselected to avoid overwriting user edits
  const [hasAutoPreselected, setHasAutoPreselected] = useState(false);

  // Load data when dialog opens
  useEffect(() => {
    if (!open) return;
    setStep(editing ? 1 : 0);
    setCanonicalDriverId(editing?.id ?? '');
    setAliasDriverIds(editing?.aliasDriverIds ?? []);
    setSessionDriverIds(editing?.sessionDriverIds ?? []);
    setCanonicalSearch('');
    setCanonicalPage(0);
    setRaceSearch('');
    setSessionSearch('');
    setHasAutoPreselected(!!editing);

    rpc<RaceDriver[]>('ListRaceDriverNames').then(drivers =>
      setAllRaceDrivers([...drivers].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      }))
    ).catch(() => setAllRaceDrivers([]));
  }, [open, editing]);

  // The selected canonical driver object
  const selectedCanonical = useMemo(
    () => allRaceDrivers.find(d => d.id === canonicalDriverId) ?? null,
    [allRaceDrivers, canonicalDriverId]
  );

  const canonicalFullName = useMemo(() => {
    if (!selectedCanonical) return '';
    return `${selectedCanonical.firstName} ${selectedCanonical.lastName}`.trim();
  }, [selectedCanonical]);

  // Query server suggestions when canonical driver is chosen
  useEffect(() => {
    if (!canonicalFullName) {
      setSuggestedRaceDrivers([]);
      setSuggestedSessionDrivers([]);
      return;
    }
    let cancelled = false;
    rpc<{ raceDrivers: SuggestedRaceDriver[]; sessionDrivers: SuggestedSessionDriver[] }>(
      'SuggestDriverAliases',
      { name: canonicalFullName, threshold: 0.1, limit: 100 }
    ).then(res => {
      if (cancelled) return;
      const race = (res.raceDrivers || []).filter(d => d.id !== canonicalDriverId);
      const sess = res.sessionDrivers || [];
      setSuggestedRaceDrivers(race);
      setSuggestedSessionDrivers(sess);

      if (!hasAutoPreselected && !editing) {
        setHasAutoPreselected(true);
        const preRace = race.filter(d => d.similarity >= SIMILARITY_THRESHOLD).map(d => d.id);
        const preSess = sess.filter(d => d.similarity >= SIMILARITY_THRESHOLD).map(d => d.id);
        setAliasDriverIds(preRace);
        setSessionDriverIds(preSess);
      }
    }).catch(() => {
      if (!cancelled) {
        setSuggestedRaceDrivers([]);
        setSuggestedSessionDrivers([]);
      }
    });
    return () => { cancelled = true; };
  }, [canonicalFullName, canonicalDriverId, hasAutoPreselected, editing]);

  // ── Step 1: Filtered + paginated canonical drivers ──────────
  const filteredCanonical = useMemo(() => {
    if (!canonicalSearch) return allRaceDrivers;
    const lower = canonicalSearch.toLowerCase();
    return allRaceDrivers.filter(d =>
      `${d.firstName} ${d.lastName}`.toLowerCase().includes(lower) ||
      d.nationality.toLowerCase().includes(lower)
    );
  }, [allRaceDrivers, canonicalSearch]);

  const totalCanonicalPages = Math.max(1, Math.ceil(filteredCanonical.length / PAGE_SIZE));
  const pagedCanonical = useMemo(
    () => filteredCanonical.slice(canonicalPage * PAGE_SIZE, (canonicalPage + 1) * PAGE_SIZE),
    [filteredCanonical, canonicalPage]
  );

  // Reset page when search changes
  useEffect(() => { setCanonicalPage(0); }, [canonicalSearch]);

  // ── Step 2: Race drivers and session drivers from server ──────────
  const filteredRaceDrivers = useMemo(() => {
    if (!raceSearch) return suggestedRaceDrivers;
    const lower = raceSearch.toLowerCase();
    return suggestedRaceDrivers.filter(d =>
      `${d.firstName} ${d.lastName}`.toLowerCase().includes(lower)
    );
  }, [suggestedRaceDrivers, raceSearch]);

  const filteredSessionDrivers = useMemo(() => {
    if (!sessionSearch) return suggestedSessionDrivers;
    const lower = sessionSearch.toLowerCase();
    return suggestedSessionDrivers.filter(d =>
      d.displayName.toLowerCase().includes(lower)
    );
  }, [suggestedSessionDrivers, sessionSearch]);

  const toggleAliasDriver = (id: string) => {
    setAliasDriverIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSessionDriver = (id: string) => {
    setSessionDriverIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const goToStep2 = () => {
    setStep(1);
  };

  const handleSubmit = () => {
    onSave({ canonicalDriverId, aliasDriverIds, sessionDriverIds });
  };

  const selectCanonical = (id: string) => {
    setCanonicalDriverId(id);
  };

  // ── Render ──────────────────────────────────────────────────
  const stepIndicator = (
    <div className="flex items-center gap-2 py-3">
      <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 0 ? 'text-accent' : 'text-text-muted'}`}>
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold
          ${step === 0 ? 'bg-accent text-white' : 'bg-bg-hover text-text-muted'}`}>
          {step > 0 ? <Check size={10} /> : '1'}
        </span>
        {t('drivers.stepCanonical')}
      </div>
      <ChevronRight size={12} className="text-text-muted" />
      <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 1 ? 'text-accent' : 'text-text-muted'}`}>
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold
          ${step === 1 ? 'bg-accent text-white' : 'bg-bg-hover text-text-muted'}`}>
          2
        </span>
        {t('drivers.stepAliases')}
      </div>
    </div>
  );

  const footer = (
    <>
      {step === 0 && (
        <button
          onClick={goToStep2}
          disabled={!canonicalDriverId}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
            hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer inline-flex items-center gap-1"
        >
          {t('common.next')}
          <ChevronRight size={12} />
        </button>
      )}
      {step === 1 && (
        <>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
              hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
          >
            {editing ? t('common.save') : t('common.create')}
          </button>
          {!editing && (
            <button
              onClick={() => setStep(0)}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
                border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <ChevronLeft size={12} />
              {t('common.back')}
            </button>
          )}
        </>
      )}
      <button
        onClick={onClose}
        className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
          border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer"
      >
        {t('common.cancel')}
      </button>
    </>
  );

  const title = editing
    ? t('drivers.editTitle')
    : step === 0
      ? t('drivers.createTitle')
      : t('drivers.createTitle');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={step === 1 && selectedCanonical ? `${selectedCanonical.firstName} ${selectedCanonical.lastName}` : undefined}
      stickyContent={stepIndicator}
      footer={footer}
    >
      {step === 0 && (
        <div className="flex flex-col gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder={t('drivers.searchDrivers')}
              value={canonicalSearch}
              onChange={e => setCanonicalSearch(e.target.value)}
              autoFocus
              className="w-full pl-8 pr-3 py-2 text-sm bg-bg-raised border border-border-subtle rounded-md
                text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
            />
          </div>

          {/* Driver list */}
          <div className="rounded-lg border border-border-subtle bg-bg-raised overflow-hidden">
            {pagedCanonical.length === 0 ? (
              <p className="text-xs text-text-muted italic p-4">{t('drivers.noRaceDrivers')}</p>
            ) : (
              pagedCanonical.map(driver => {
                const isSelected = driver.id === canonicalDriverId;
                return (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() => selectCanonical(driver.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer
                      border-b border-border-subtle last:border-b-0
                      ${isSelected
                        ? 'bg-accent/10 border-l-2 border-l-accent'
                        : 'hover:bg-bg-hover border-l-2 border-l-transparent'
                      }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${isSelected ? 'text-accent font-medium' : 'text-text-primary'}`}>
                        {driver.firstName} {driver.lastName}
                      </div>
                      {driver.nationality && (
                        <div className="text-[0.65rem] text-text-muted">{driver.nationality}</div>
                      )}
                    </div>
                    {isSelected && (
                      <Check size={14} className="text-accent shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalCanonicalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-text-muted">
              <button
                onClick={() => setCanonicalPage(p => Math.max(0, p - 1))}
                disabled={canonicalPage === 0}
                className="px-2 py-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronLeft size={14} />
              </button>
              <span>
                {canonicalPage + 1} / {totalCanonicalPages}
                <span className="ml-1.5 text-text-muted">
                  ({filteredCanonical.length} {t('drivers.raceDrivers').toLowerCase()})
                </span>
              </span>
              <button
                onClick={() => setCanonicalPage(p => Math.min(totalCanonicalPages - 1, p + 1))}
                disabled={canonicalPage >= totalCanonicalPages - 1}
                className="px-2 py-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Race Drivers (Aliases) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-text-muted">
                {t('drivers.raceDrivers')}
                {aliasDriverIds.length > 0 && (
                  <span className="ml-1.5 text-accent">({aliasDriverIds.length})</span>
                )}
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder={t('drivers.searchDrivers')}
                  value={raceSearch}
                  onChange={e => setRaceSearch(e.target.value)}
                  autoFocus
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-bg-raised border border-border-subtle rounded-md
                    text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
                />
              </div>

              <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border-subtle bg-bg-raised p-1 space-y-0.5 mt-1">
                {filteredRaceDrivers.length === 0 ? (
                  <p className="text-xs text-text-muted italic px-2 py-1">{t('drivers.noRaceDrivers')}</p>
                ) : (
                  filteredRaceDrivers.map(driver => {
                    const isChecked = aliasDriverIds.includes(driver.id);
                    const simPct = Math.round(driver.similarity * 100);
                    return (
                      <label
                        key={driver.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors cursor-pointer
                          ${isChecked ? 'bg-accent/8' : 'hover:bg-bg-hover'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleAliasDriver(driver.id)}
                          className="accent-accent shrink-0"
                        />
                        <span className="text-sm text-text-primary truncate flex-1">
                          {driver.firstName} {driver.lastName}
                        </span>
                        {simPct >= 40 && (
                          <span className={`text-[0.6rem] shrink-0 px-1 py-0.5 rounded
                            ${simPct >= 80 ? 'text-green bg-green/10' : simPct >= 60 ? 'text-yellow bg-yellow/10' : 'text-text-muted bg-bg-hover'}`}>
                            {simPct}%
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Session Drivers */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-text-muted">
                {t('drivers.sessionDrivers')}
                {sessionDriverIds.length > 0 && (
                  <span className="ml-1.5 text-accent">({sessionDriverIds.length})</span>
                )}
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder={t('drivers.searchDrivers')}
                  value={sessionSearch}
                  onChange={e => setSessionSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-bg-raised border border-border-subtle bg-bg-raised rounded-md
                    text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
                />
              </div>

              <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border-subtle bg-bg-raised p-1 space-y-0.5 mt-1">
                {filteredSessionDrivers.length === 0 ? (
                  <p className="text-xs text-text-muted italic px-2 py-1">{t('drivers.noSessionDrivers')}</p>
                ) : (
                  filteredSessionDrivers.map(driver => {
                    const isChecked = sessionDriverIds.includes(driver.id);
                    const simPct = Math.round(driver.similarity * 100);
                    return (
                      <label
                        key={driver.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors cursor-pointer
                          ${isChecked ? 'bg-accent/8' : 'hover:bg-bg-hover'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSessionDriver(driver.id)}
                          className="accent-accent shrink-0"
                        />
                        <span className="text-sm text-text-primary truncate flex-1">
                          {driver.displayName}
                        </span>
                        {simPct >= 40 && (
                          <span className={`text-[0.6rem] shrink-0 px-1 py-0.5 rounded
                            ${simPct >= 80 ? 'text-green bg-green/10' : simPct >= 60 ? 'text-yellow bg-yellow/10' : 'text-text-muted bg-bg-hover'}`}>
                            {simPct}%
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
